import React, { useState, useRef, useEffect, useMemo } from "react";
import styled, { createGlobalStyle } from "styled-components";
import ModalBasic from "./ModalBasic.jsx";
import closeButton from "../../img/close_button.png";
import pageNextButton from "../../img/pageNextButton.png";
import pagePrevButton from "../../img/pagePrevButton.png";
import copy from "../../img/copy_black.png";
import guide1 from "../../img/modal_precedent_guide_step1_pc.png";
import guide2 from "../../img/modal_precedent_guide_step2_pc.png";
import guide1Mobile from "../../img/modal_precedent_guide_step1_mobile.png";
import guide2Mobile from "../../img/modal_precedent_guide_step2_mobile.png";
import guide1Tablet from "../../img/modal_precedent_guide_step1_tablet.png";
import guide2Tablet from "../../img/modal_precedent_guide_step2_tablet.png";
import HeightInterval from "../HeightInterval.jsx";
import { useMediaQuery } from "react-responsive";
import { useSwipeable } from "react-swipeable";
import criminalKeywordsData from "../../data/criminalCaseKeywords.json";



const ModalPrecedentGuide = ({ isOpen, onClose, courtName, caseNumber }) => {
  const [currentPage, setCurrentPage] = useState(1); // 1 또는 2
  const totalPages = 2;
  const isMobile = useMediaQuery({ maxWidth: 480 });
  const isTablet = useMediaQuery({ minWidth: 481, maxWidth: 1024 });
  
  // 사건번호의 사건구분 기호로 유형 판단 (예: '2024고단123' -> '고단')
  const caseType = useMemo(() => {
    if (!caseNumber) return "민사·행정·특허·선거특별";
    
    const caseMatch = caseNumber.match(/\d{2,4}\s*([가-힣]+)\s*\d+/);
    if (!caseMatch) return "민사·행정·특허·선거특별";
    
    const isCriminal = criminalKeywordsData.criminalKeywords.includes(caseMatch[1]);
    
    return isCriminal ? "형사" : "민사·행정·특허·선거특별";
  }, [caseNumber]);
  
  // 모바일 슬라이드 관련 상태
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [imageWidth, setImageWidth] = useState(0);
  const imageContainerRef = useRef(null);

  // 이미지 너비 측정
  useEffect(() => {
    const updateImageWidth = () => {
      if (imageContainerRef.current) {
        setImageWidth(imageContainerRef.current.offsetWidth);
      }
    };

    if ((isMobile || isTablet) && isOpen) {
      updateImageWidth();
      window.addEventListener('resize', updateImageWidth);
      return () => window.removeEventListener('resize', updateImageWidth);
    }
  }, [isMobile, isTablet, isOpen]);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getCurrentImage = () => {
    return currentPage === 1 ? guide1 : guide2;
  };

  // 사건번호 복사 함수
  const handleCopyCaseNumber = async () => {
    try {
      await navigator.clipboard.writeText(caseNumber);
      alert("복사되었습니다");
    } catch (err) {
      // Clipboard API가 실패할 경우 fallback 방법 사용
      const textArea = document.createElement("textarea");
      textArea.value = caseNumber;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        alert("복사되었습니다");
      } catch (fallbackErr) {
        alert("복사에 실패했습니다");
      }
      document.body.removeChild(textArea);
    }
  };

  // 원문 검색 사이트 바로가기
  const handleGoToSearchSite = () => {
    window.open("https://portal.scourt.go.kr/pgp/index.on?m=PGP202M01&l=Y&c=400", "_blank");
  };

  // 이미지 영역 스와이프 핸들러 (SuggestedAskCards.jsx 방식)
  const imageSwipeHandlers = useSwipeable({
    onSwipeStart: (eventData) => {
      // 세로 스와이프인지 확인
      const absX = Math.abs(eventData.deltaX);
      const absY = Math.abs(eventData.deltaY);
      
      // 세로 스와이프이면 무시
      if (absY > absX) {
        return;
      }
      
      setIsDragging(true);
    },
    onSwiping: (eventData) => {
      // 세로 스와이프인지 확인
      const absX = Math.abs(eventData.deltaX);
      const absY = Math.abs(eventData.deltaY);
      
      // 세로 스와이프이면 무시
      if (absY > absX) {
        return;
      }
      
      // 드래그 중일 때 오프셋 계산
      const deltaX = eventData.deltaX;
      const maxOffset = imageWidth * 0.3; // 최대 30%까지만 이동
      const clampedOffset = Math.max(-maxOffset, Math.min(maxOffset, deltaX));
      setDragOffset(clampedOffset);
    },
    onSwipedLeft: () => {
      if (currentPage < totalPages) {
        setCurrentPage(prev => prev + 1);
      }
      setIsDragging(false);
      setDragOffset(0);
    },
    onSwipedRight: () => {
      if (currentPage > 1) {
        setCurrentPage(prev => prev - 1);
      }
      setIsDragging(false);
      setDragOffset(0);
    },
    onTouchEndOrOnMouseUp: () => {
      setIsDragging(false);
      setDragOffset(0);
    },
    trackTouch: true,
    preventScrollOnSwipe: false,
    delta: 15,
  });

  // 모달이 열릴 때 페이지를 1로 리셋
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setIsDragging(false);
      setDragOffset(0);
    }
  }, [isOpen]);

  return (
    <>
      {isMobile && <MobileModalStyle />}
      <ModalBasic
        isOpen={isOpen}
        onClose={onClose}
        width={
          isMobile
            ? "312px"
            : isTablet
              ? "552px"
              : (caseType === "민사·행정·특허·선거특별" ? "1080px" : "1000px")
        }
        height={
          isMobile
            ? "631px"
            : isTablet
              ? "680px"
              : (caseType === "민사·행정·특허·선거특별" ? "620px" : "600px")
        }
        className={isMobile ? "mobile-precedent-guide-modal" : ""}
      >
      {isMobile ? (
        <MobileContainer>
          <MobileCloseButton onClick={onClose}>
            <img
              src={closeButton}
              alt="닫기 버튼"
              width="24px"
              height="24px"
            />
          </MobileCloseButton>

          <MobileTitle>판례 원문 검색 가이드 Tip</MobileTitle>

          {/* 선택한 판례 정보 */}
          <MobileCaseInfoSection>
            <MobileCaseInfoTitle>[선택한 판례 정보]</MobileCaseInfoTitle>
            <MobileCaseInfoList>
              <MobileCaseInfoItem>
                <MobileCaseInfoLabel>유형</MobileCaseInfoLabel>
                <MobileCaseInfoValue>
                  {caseType === "민사·행정·특허·선거특별" ? (
                    <>민사·행정·<br />특허·선거특별</>
                  ) : (
                    caseType
                  )}
                </MobileCaseInfoValue>
              </MobileCaseInfoItem>
              <MobileCaseInfoItem>
                <MobileCaseInfoLabel>법원</MobileCaseInfoLabel>
                <MobileCaseInfoValue>{courtName}</MobileCaseInfoValue>
              </MobileCaseInfoItem>
              <MobileCaseInfoItem>
                <MobileCaseInfoLabel>사건번호</MobileCaseInfoLabel>
                <MobileCaseInfoValue>
                  <MobileCaseNumber>{caseNumber}</MobileCaseNumber>
                  <MobileCopyIcon src={copy} alt="복사" onClick={handleCopyCaseNumber} />
                </MobileCaseInfoValue>
              </MobileCaseInfoItem>
            </MobileCaseInfoList>
          </MobileCaseInfoSection>

          {/* 이미지 영역 - 실시간 스와이프 효과 */}
          <MobileImageWrapper
            ref={imageContainerRef}
            {...imageSwipeHandlers}
          >
            <MobileImageSlideContainer
              currentPage={currentPage}
              dragOffset={dragOffset}
              isDragging={isDragging}
              imageWidth={imageWidth}
            >
              <MobileImagePage>
                <MobileImageContainer>
                  <MobileGuideImage src={guide1Mobile} alt="판례 원문 검색 가이드 1단계" />
                </MobileImageContainer>
              </MobileImagePage>
              <MobileImagePage>
                <MobileImageContainer>
                  <MobileGuideImage src={guide2Mobile} alt="판례 원문 검색 가이드 2단계" />
                </MobileImageContainer>
              </MobileImagePage>
            </MobileImageSlideContainer>
          </MobileImageWrapper>

          {/* 페이지 인디케이터 - 이미지와 설명 사이 */}
          <MobilePageIndicator>
            {[1, 2].map((page) => (
              <MobileDot key={page} active={currentPage === page} />
            ))}
          </MobilePageIndicator>

          {/* 설명 영역 - 페이지 변경 시 내용만 바뀜 (페이드 효과) */}
          <MobileStepsContainer>
            {currentPage === 1 ? (
              <MobileStepsBox key="page1">
                <MobileStepItem>
                  <MobileStepNumber>1</MobileStepNumber>
                  <MobileStepText>개인정보 수집 동의</MobileStepText>
                </MobileStepItem>
                <MobileStepItem>
                  <MobileStepNumber>2</MobileStepNumber>
                  <MobileStepText>본인 실명확인</MobileStepText>
                </MobileStepItem>
              </MobileStepsBox>
            ) : (
              <MobileStepsBoxScrollable key="page2">
                <MobileStepItem>
                  <MobileStepNumber>3</MobileStepNumber>
                  <MobileStepText>민사/형사 구분 선택</MobileStepText>
                </MobileStepItem>
                <MobileStepItem>
                  <MobileStepNumber>4</MobileStepNumber>
                  <MobileStepText>사건번호 입력 (선택 및 직접 입력)</MobileStepText>
                </MobileStepItem>
                <MobileStepItem>
                  <MobileStepNumber>5</MobileStepNumber>
                  <MobileStepText>원문 검색 버튼 클릭</MobileStepText>
                </MobileStepItem>
                <MobileStepItem>
                  <MobileStepNumber>6</MobileStepNumber>
                  <MobileStepText>원하는 판례 체크박스 선택 후 결제 버튼 클릭 (건당 1000원)</MobileStepText>
                </MobileStepItem>
                <MobileStepItem>
                  <MobileStepNumber>7</MobileStepNumber>
                  <MobileStepText>판례 원문 검색 완료</MobileStepText>
                </MobileStepItem>
              </MobileStepsBoxScrollable>
            )}
          </MobileStepsContainer>

          <MobileActionButton onClick={handleGoToSearchSite}>원문 검색 사이트 바로가기</MobileActionButton>
        </MobileContainer>
      ) : isTablet ? (
        <TabletContainer>
          <TabletCloseButton onClick={onClose}>
            <img
              src={closeButton}
              alt="닫기 버튼"
              width="24px"
              height="24px"
            />
          </TabletCloseButton>

          <TabletTitle>판례 원문 검색 가이드 Tip</TabletTitle>
          <TabletDivider />

          <TabletCaseInfoSection>
            <TabletCaseInfoTitle>[선택한 판례 정보]</TabletCaseInfoTitle>
            <TabletCaseInfoList>
              <TabletCaseInfoItem>
                <TabletCaseInfoLabel>유형</TabletCaseInfoLabel>
                <TabletCaseInfoValue>
                  {caseType === "민사·행정·특허·선거특별" ? (
                    <>민사·행정·<br />특허·선거특별</>
                  ) : (
                    caseType
                  )}
                </TabletCaseInfoValue>
              </TabletCaseInfoItem>
              <TabletCaseInfoItem>
                <TabletCaseInfoLabel>법원</TabletCaseInfoLabel>
                <TabletCaseInfoValue>{courtName}</TabletCaseInfoValue>
              </TabletCaseInfoItem>
              <TabletCaseInfoItem>
                <TabletCaseInfoLabel>사건번호</TabletCaseInfoLabel>
                <TabletCaseInfoValue>
                  <TabletCaseNumber>{caseNumber}</TabletCaseNumber>
                  <TabletCopyIcon src={copy} alt="복사" onClick={handleCopyCaseNumber} />
                </TabletCaseInfoValue>
              </TabletCaseInfoItem>
            </TabletCaseInfoList>
          </TabletCaseInfoSection>

          <TabletImageWrapper
            ref={imageContainerRef}
            {...imageSwipeHandlers}
          >
            <TabletImageSlideContainer
              currentPage={currentPage}
              dragOffset={dragOffset}
              isDragging={isDragging}
              imageWidth={imageWidth}
            >
              <TabletImagePage>
                <TabletImageContainer>
                  <TabletGuideImage src={guide1Tablet} alt="판례 원문 검색 가이드 1단계" />
                </TabletImageContainer>
              </TabletImagePage>
              <TabletImagePage>
                <TabletImageContainer>
                  <TabletGuideImage src={guide2Tablet} alt="판례 원문 검색 가이드 2단계" />
                </TabletImageContainer>
              </TabletImagePage>
            </TabletImageSlideContainer>

            <PageIndicator>
              {currentPage === 1 ? (
                <>
                  <PageText>
                    {currentPage} / {totalPages}
                  </PageText>
                  <PageImageButton
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                  >
                    <PageButtonImage src={pageNextButton} alt="다음 페이지" />
                  </PageImageButton>
                </>
              ) : (
                <>
                  <PageImageButton
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                  >
                    <PageButtonImage src={pagePrevButton} alt="이전 페이지" />
                  </PageImageButton>
                  <PageText>
                    {currentPage} / {totalPages}
                  </PageText>
                </>
              )}
            </PageIndicator>
          </TabletImageWrapper>

          <TabletStepsContainer>
            {currentPage === 1 ? (
              <TabletStepsBox key="tablet-page1">
                <TabletStepItem>
                  <TabletStepNumber>1</TabletStepNumber>
                  <TabletStepText>개인정보 수집 동의</TabletStepText>
                </TabletStepItem>
                <TabletStepItem>
                  <TabletStepNumber>2</TabletStepNumber>
                  <TabletStepText>본인 실명확인</TabletStepText>
                </TabletStepItem>
              </TabletStepsBox>
            ) : (
              <TabletStepsBoxScrollable key="tablet-page2">
                <TabletStepItem>
                  <TabletStepNumber>3</TabletStepNumber>
                  <TabletStepText>민사/형사 구분 선택</TabletStepText>
                </TabletStepItem>
                <TabletStepItem>
                  <TabletStepNumber>4</TabletStepNumber>
                  <TabletStepText>사건번호 입력 (선택 및 직접 입력)</TabletStepText>
                </TabletStepItem>
                <TabletStepItem>
                  <TabletStepNumber>5</TabletStepNumber>
                  <TabletStepText>원문 검색 버튼 클릭</TabletStepText>
                </TabletStepItem>
                <TabletStepItem>
                  <TabletStepNumber>6</TabletStepNumber>
                  <TabletStepText>원하는 판례 체크박스 선택 후 결제 버튼 클릭 (건당 1000원)</TabletStepText>
                </TabletStepItem>
                <TabletStepItem>
                  <TabletStepNumber>7</TabletStepNumber>
                  <TabletStepText>판례 원문 검색 완료</TabletStepText>
                </TabletStepItem>
              </TabletStepsBoxScrollable>
            )}
          </TabletStepsContainer>

          <TabletActionButton onClick={handleGoToSearchSite}>원문 검색 사이트 바로가기</TabletActionButton>
        </TabletContainer>
      ) : (
        <Container>
        <CloseButton onClick={onClose}>
          <img
            src={closeButton}
            alt="닫기 버튼"
            width="32px"
            height="32px"
          />
        </CloseButton>

        <Title>판례 원문 검색 가이드 Tip</Title>

        <ContentWrapper>
          {/* 왼쪽: 이미지 영역 */}
          <ImageSection>
            <ImageContainer>
              <PageIndicator>
                {currentPage === 1 ? (
                  <>
                    <PageText>
                      {currentPage} / {totalPages}
                    </PageText>
                    <PageImageButton
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                    >
                      <PageButtonImage src={pageNextButton} alt="다음 페이지" />
                    </PageImageButton>
                  </>
                ) : (
                  <>
                    <PageImageButton
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                    >
                      <PageButtonImage src={pagePrevButton} alt="이전 페이지" />
                    </PageImageButton>
                    <PageText>
                      {currentPage} / {totalPages}
                    </PageText>
                  </>
                )}
              </PageIndicator>
              <GuideImage src={getCurrentImage()} alt="판례 원문 검색 가이드" />
            </ImageContainer>
          </ImageSection>

          {/* 오른쪽: 가이드 정보 */}
          <GuideSection>
            <GuideTitle>[선택한 판례 정보]</GuideTitle>
            <CaseInfo>
              <InfoItem>
                <InfoLabel>유형</InfoLabel>
                <InfoValue>
                  {caseType === "민사·행정·특허·선거특별" ? (
                    <>민사·행정·<br />특허·선거특별</>
                  ) : (
                    caseType
                  )}
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>법원</InfoLabel>
                <InfoValue>{courtName}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>사건번호</InfoLabel>
                <InfoValue>
                  <CaseNumber>{caseNumber}</CaseNumber>
                  <CopyIcon src={copy} alt="복사" onClick={handleCopyCaseNumber} />
                </InfoValue>
              </InfoItem>
            </CaseInfo>

            <HrStyles />

            <StepsWrapper>
              <StepsList>
                <StepItem page={1} currentPage={currentPage} isHighlighted={currentPage === 1}>
                  <StepNumber>1</StepNumber>
                  <StepText>개인정보 수집 동의</StepText>
                </StepItem>
                <StepItem page={1} currentPage={currentPage} isHighlighted={currentPage === 1}>
                  <StepNumber>2</StepNumber>
                  <StepText>본인 실명확인</StepText>
                </StepItem>
                <HeightInterval height={10} />
                <StepItem page={2} currentPage={currentPage} isHighlighted={currentPage === 2}>
                  <StepNumber>3</StepNumber>
                  <StepText>민사/형사 구분 선택</StepText>
                </StepItem>
                <StepItem page={2} currentPage={currentPage} isHighlighted={currentPage === 2}>
                  <StepNumber>4</StepNumber>
                  <StepText>사건번호 입력 (선택 및 직접 입력)</StepText>
                </StepItem>
                <StepItem page={2} currentPage={currentPage} isHighlighted={currentPage === 2}>
                  <StepNumber>5</StepNumber>
                  <StepText>원문 검색 버튼 클릭</StepText>
                </StepItem>
                <StepItem page={2} currentPage={currentPage} isHighlighted={currentPage === 2}>
                  <StepNumber>6</StepNumber>
                  <StepText>원하는 판례 체크박스 선택 후 결제 버튼 클릭 (건당 1000원)</StepText>
                </StepItem>
                <StepItem page={2} currentPage={currentPage} isHighlighted={currentPage === 2}>
                  <StepNumber>7</StepNumber>
                  <StepText>판례 원문 검색 완료</StepText>
                </StepItem>
              </StepsList>
              <StepsContainer currentPage={currentPage} />
            </StepsWrapper>

            <ActionButton onClick={handleGoToSearchSite}>원문 검색 사이트 바로가기</ActionButton>
          </GuideSection>
        </ContentWrapper>
      </Container>
      )}
      </ModalBasic>
    </>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  position: relative;
  width: 100%;
  //height: 100%;
  display: flex;
  flex-direction: column;
  padding-top: 34px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 10;
  
  &:hover {
    opacity: 0.8;
  }
`;

const Title = styled.h1`
  margin: 0 0 28px 0;
  color: #000;
  text-align: center;
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`;

const ContentWrapper = styled.div`
  display: flex;
  gap: 24px;
  flex: 1;
  overflow: hidden;
  background-color: #F4F4F4;
  padding: 32px 30px 44px 40px;
`;

const ImageSection = styled.div`
  width: 516px;
  height: 433px;
  aspect-ratio: 516/433;
  border-radius: 24px;
  //border: 0.3px solid rgba(0, 0, 0, 0.20);
  // background: lightgray;
  position: relative;
  // overflow: hidden;
  //margin: 32px 0 0 40px;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const PageIndicator = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  z-index: 10;
`;

const PageImageButton = styled.button`
  display: flex;
  width: 32px;
  height: 32px;
  padding: 0;
  justify-content: center;
  align-items: center;
  border: none;
  background: none;
  cursor: pointer;
  
  &:hover:not(:disabled) {
    opacity: 0.85;
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PageButtonImage = styled.img`
  width: 32px;
  height: 32px;
  display: block;
`;

const PageText = styled.span`
  display: inline-flex;
  padding: 6px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 24px;
  background: rgba(0, 0, 0, 0.60);
  color: #fff;
  font-family: Pretendard;
  font-size: 12.19px;
  font-weight: 500;
  text-align: center;
  line-height: 19.81px;
`;

const GuideImage = styled.img`
  width: 102.798%;
  height: 100.693%;
  object-fit: cover;
  object-position: -6.975px 0px;
  position: absolute;
  top: 0;
  left: 0;
`;

const GuideSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  // gap: 24px;
  // background: #f8f9fa;
  border-radius: 8px;
`;

const GuideTitle = styled.h2`
  margin: 0;
  color: rgba(0, 0, 0, 0.30);
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 26px;
`;

const CaseInfo = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: center;
  padding: 0;
`;

const InfoItem = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const InfoLabel = styled.span`
  color: rgba(0, 0, 0, 0.3);
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
`;

const InfoValue = styled.span`
  color: #000;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CaseNumber = styled.span`
  text-decoration: underline;
`;

const CopyIcon = styled.img`
  cursor: pointer;
  width: 12px;
  height: 14.4px;
  
  &:hover {
    opacity: 0.7;
  }
`;

const HrStyles = styled.hr`
  width:390px;
  height: 0px;
  flex-shrink: 0;
  border: 1px solid rgba(0, 0, 0, 0.10);
  margin: 13px 0 16px 0;
`;

const StepsWrapper = styled.div`
  position: relative;
  width: 388px;
`;

const StepsList = styled.ol`
  margin: 18px 0 0 20px;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StepItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  // padding: 4px 0;
  margin: 0;
  // min-height: 29px;
  color: rgba(0, 0, 0, 0.70);
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.28px;
  transition: color 0.2s ease;
  position: relative;
  z-index: 2;
`;

const StepsContainer = styled.div`
  position: absolute;
  top: ${props => {
    // 각 StepItem의 실제 높이 계산
    // padding: 4px (top) + 4px (bottom) = 8px
    // line-height: 14px * 1.5 = 21px
    // 실제 높이: 8px + 21px = 29px (min-height와 동일)
    // const itemHeight = 29;
    // const gap = 8;
    // const containerPadding = 16;
    
    if (props.currentPage === 1) {
      // 1/2 페이지: 1-2번 항목 감싸기
      // 첫 번째 항목: 0부터 시작
      // StepsContainer의 padding 16px을 고려하여 top을 -16px로 설정하면
      // 내부 padding 16px부터 첫 번째 항목이 시작됨
      // return '-16px';
      return `0`;
    } else {
      // 2/2 페이지: 3-7번 항목 감싸기
      // 첫 번째 항목: 0 ~ 29px
      // gap: 8px
      // 두 번째 항목: 37px ~ 66px
      // gap: 8px
      // 세 번째 항목 시작: 74px
      // StepsContainer가 세 번째 항목부터 시작하도록 하려면
      // 두 번째 항목이 완전히 끝난 후 gap 다음부터 시작해야 함
      // 두 번째 항목 끝: 66px
      // gap: 8px
      // 세 번째 항목 시작: 74px
      // StepsContainer의 padding 16px을 고려하면, 세 번째 항목이 내부 16px 위치에 오도록
      // 하지만 2번부터 감싸고 있다는 것은 top이 너무 높다는 의미
      // 실제 항목 높이가 정확히 29px이 아닐 수 있으므로, 더 정확한 계산 필요
      // 두 번째 항목의 끝 위치를 정확히 계산하고, 그 다음 gap을 더한 후 padding을 빼야 함
      // const secondItemEnd = itemHeight + gap + itemHeight; // 66px
      // const thirdItemStart = secondItemEnd + gap; // 74px
      // StepsContainer의 padding을 고려하여 top 위치 계산
      // 세 번째 항목이 StepsContainer 내부의 16px 위치에 오도록
      // 하지만 실제로는 더 낮춰야 할 수도 있음
      // 두 번째 항목이 완전히 끝난 후 gap 다음부터 시작하도록 더 정확하게 계산
      return `88px`; // 66px (더 낮춤)
    }
  }};
  left: 0;
  width: 388px;
  height: ${props => props.currentPage === 1 ? '90px' : '192px'};
  border-radius: 16px;
  border: 1px dashed rgba(0, 0, 0, 0.30);
  background: #FFF;
  padding: 16px;
  box-sizing: border-box;
  pointer-events: none;
  z-index: 1;
`;

const StepNumber = styled.span`
  display: flex;
  min-width: 16px;
  min-height: 16px;
  padding: 4px 7px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 10px;
  background: #000;
  color: #FFF;
  font-family: Pretendard;
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: -0.2px;
  flex-shrink: 0;
  box-sizing: border-box;
`;

const StepText = styled.span`
  flex: 1;
`;

const ActionButton = styled.button`
  width: 390px;
  height: 48px;
  padding: 15px 0;
  background: #000;
  border: none;
  border-radius: 12px;
  color: #fff;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 600;
  line-height: normal;
  letter-spacing: -0.32px;
  cursor: pointer;
  transition: background 0.2s ease;
  margin-top: 32px;
  &:hover {
    background: #333;
  }
`;

// 모바일 스타일 컴포넌트들
const MobileContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 20px;
  box-sizing: border-box;
  overflow: hidden;
`;

const MobileCloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 10;
  
  &:hover {
    opacity: 0.8;
  }
`;

const MobileTitle = styled.h1`
  margin: 0 0 20px 0;
  color: #000;
  text-align: center;
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;

const MobileCaseInfoSection = styled.div`
  width: 100%;
  margin-bottom: 16px;
`;

const MobileCaseInfoTitle = styled.h2`
  color: rgba(0, 0, 0, 0.30);
  font-family: Pretendard;
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  margin: 0 0 8px 0;
`;

const MobileCaseInfoList = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const MobileCaseInfoItem = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const MobileCaseInfoLabel = styled.span`
  color: rgba(0, 0, 0, 0.30);
  font-family: Pretendard;
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`;

const MobileCaseInfoValue = styled.span`
  color: rgba(0, 0, 0, 0.70);
  font-family: Pretendard;
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MobileCaseNumber = styled.span`
  color: rgba(0, 0, 0, 0.70);
  font-family: Pretendard;
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
`;

const MobileCopyIcon = styled.img`
  cursor: pointer;
  width: 12px;
  height: 14.4px;
  
  &:hover {
    opacity: 0.7;
  }
`;

const MobileImageWrapper = styled.div`
  width: 272px;
  height: 228px;
  margin: 0 auto 16px auto;
  overflow: hidden;
  position: relative;
`;

const MobileImageSlideContainer = styled.div`
  display: flex;
  width: ${props => props.imageWidth * 2 || 544}px;
  height: 100%;
  transform: translateX(${props => {
    const baseOffset = -(props.currentPage - 1) * (props.imageWidth || 272);
    const dragOffset = props.isDragging ? props.dragOffset : 0;
    return baseOffset + dragOffset;
  }}px);
  transition: ${props => props.isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'};
`;

const MobileImagePage = styled.div`
  width: 272px;
  height: 100%;
  flex-shrink: 0;
`;

const MobileImageContainer = styled.div`
  width: 272px;
  height: 228px;
  aspect-ratio: 68/57;
  border-radius: 16px;
  border: 0.3px solid rgba(0, 0, 0, 0.20);
  background: lightgray;
  position: relative;
  overflow: hidden;
  align-self: center;
`;

const MobileGuideImage = styled.img`
  width: 102.798%;
  height: 100.693%;
  object-fit: cover;
  object-position: -3.677px 0px;
  position: absolute;
  top: 0;
  left: 0;
`;

const MobileStepsContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  height: 90px;
`;

const MobileStepsBox = styled.div`
  display: flex;
  width: 272px;
  height: 90px;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  border-radius: 16px;
  border: 1px dashed rgba(0, 0, 0, 0.30);
  background: #FFF;
  box-sizing: border-box;
  animation: fadeIn 0.3s ease-in-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const MobileStepsBoxScrollable = styled.div`
  display: flex;
  width: 272px;
  height: 90px;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  border-radius: 16px;
  border: 1px dashed rgba(0, 0, 0, 0.30);
  background: #FFF;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
  animation: fadeIn 0.3s ease-in-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
  }
`;

const MobileStepItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`;

const MobileStepNumber = styled.span`
  display: flex;
  min-width: 16px;
  min-height: 16px;
  padding: 4px 7px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 10px;
  background: #000;
  color: #FFF;
  font-family: Pretendard;
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: -0.2px;
  flex-shrink: 0;
  box-sizing: border-box;
`;

const MobileStepText = styled.span`
  flex: 1;
  color: rgba(0, 0, 0, 0.70);
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: -0.28px;
`;

const MobilePageIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin: 16px 0;
`;

const MobileDot = styled.div`
  width: ${props => props.active ? '8px' : '6px'};
  height: ${props => props.active ? '8px' : '6px'};
  border-radius: 50%;
  background: ${props => props.active ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.2)'};
  transition: all 0.3s ease;
`;

const MobileActionButton = styled.button`
  width: 100%;
  height: 48px;
  padding: 15px 0;
  background: #000;
  border: none;
  border-radius: 12px;
  color: #fff;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 600;
  line-height: normal;
  letter-spacing: -0.32px;
  cursor: pointer;
  transition: background 0.2s ease;
  margin-top: auto;
  
  &:hover {
    background: #333;
  }
  
  &:active {
    background: #333;
  }
`;

const MobileModalStyle = createGlobalStyle`
  .mobile-precedent-guide-modal {
    box-shadow: 0 4px 20px 5px rgba(0, 0, 0, 0.05) !important;
  }
`;

// 태블릿 스타일 컴포넌트들
const TabletContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 20px 20px 24px 20px;
  box-sizing: border-box;
`;

const TabletCloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 10;
  
  &:hover {
    opacity: 0.8;
  }
`;

const TabletTitle = styled.h1`
  margin: 8px 0 12px 0;
  color: #000;
  text-align: center;
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;

const TabletDivider = styled.hr`
  width: 100%;
  height: 1px;
  border: none;
  background: rgba(0, 0, 0, 0.08);
  margin: 0 0 12px 0;
`;

const TabletCaseInfoSection = styled.div`
  width: 100%;
  margin-bottom: 12px;
`;

const TabletCaseInfoTitle = styled.h2`
  color: rgba(0, 0, 0, 0.30);
  font-family: Pretendard;
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  margin: 0 0 8px 0;
`;

const TabletCaseInfoList = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const TabletCaseInfoItem = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const TabletCaseInfoLabel = styled.span`
  color: rgba(0, 0, 0, 0.30);
  font-family: Pretendard;
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`;

const TabletCaseInfoValue = styled.span`
  color: rgba(0, 0, 0, 0.70);
  font-family: Pretendard;
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TabletCaseNumber = styled.span`
  text-decoration: underline;
`;

const TabletCopyIcon = styled.img`
  cursor: pointer;
  width: 12px;
  height: 14.4px;
  
  &:hover {
    opacity: 0.7;
  }
`;

const TabletImageWrapper = styled.div`
  width: 472px;
  height: 332px;
  margin: 0 auto 16px auto;
  overflow: hidden;
  border-radius: 21.953px;
  position: relative;
`;

const TabletImageSlideContainer = styled.div`
  display: flex;
  width: ${props => props.imageWidth * 2 || 944}px;
  height: 100%;
  transform: translateX(${props => {
    const baseOffset = -(props.currentPage - 1) * (props.imageWidth || 472);
    const dragOffset = props.isDragging ? props.dragOffset : 0;
    return baseOffset + dragOffset;
  }}px);
  transition: ${props => props.isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'};
`;

const TabletImagePage = styled.div`
  width: 472px;
  height: 100%;
  flex-shrink: 0;
  overflow: hidden;
`;

const TabletImageContainer = styled.div`
  width: 472px;
  height: 332px;
  aspect-ratio: 118/83;
  border-radius: 21.953px;
  border: 0.274px solid rgba(0, 0, 0, 0.20);
  background: lightgray;
  position: relative;
  overflow: hidden;
  align-self: center;
`;

const TabletGuideImage = styled.img`
  width: 102.798%;
  height: 120.127%;
  object-fit: cover;
  object-position: -6.38px 0px;
  position: absolute;
  top: 0;
  left: 0;
  display: block;
`;


const TabletStepsContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  height: 90px;
  margin-bottom: 12px;
`;

const TabletStepsBox = styled.div`
  display: flex;
  width: 472px;
  height: 90px;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  border-radius: 16px;
  border: 1px dashed rgba(0, 0, 0, 0.30);
  background: #FFF;
  box-sizing: border-box;
  animation: fadeIn 0.3s ease-in-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const TabletStepsBoxScrollable = styled.div`
  display: flex;
  width: 472px;
  height: 90px;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  border-radius: 16px;
  border: 1px dashed rgba(0, 0, 0, 0.30);
  background: #FFF;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
  animation: fadeIn 0.3s ease-in-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
  }
`;

const TabletStepItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`;

const TabletStepNumber = styled.span`
  display: flex;
  min-width: 16px;
  min-height: 16px;
  padding: 4px 7px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 10px;
  background: #000;
  color: #FFF;
  font-family: Pretendard;
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: -0.2px;
  flex-shrink: 0;
  box-sizing: border-box;
`;

const TabletStepText = styled.span`
  flex: 1;
  color: rgba(0, 0, 0, 0.70);
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: -0.28px;
`;

const TabletActionButton = styled.button`
  width: 100%;
  height: 48px;
  padding: 15px 0;
  background: #000;
  border: none;
  border-radius: 12px;
  color: #fff;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 600;
  line-height: normal;
  letter-spacing: -0.32px;
  cursor: pointer;
  transition: background 0.2s ease;
  margin-top: auto;
  
  &:hover {
    background: #333;
  }
  
  &:active {
    background: #333;
  }
`;

export default ModalPrecedentGuide;

